'use client';

import { FC, lazy, ReactNode, Suspense } from 'react';

import { AllEntryTypes } from '../../models/Content';
import {
    isContentItemBanner,
    isContentItemBlockArticles,
    isContentItemBlockContent,
    isContentItemBlockCta,
    isContentItemBlockFaq,
    isContentItemBlockLegalText,
    isContentItemBlockProducts,
    isContentItemBlockPromo,
    isContentItemBlockQuickLink,
    isContentItemBlockText,
    isContentItemBlockValuePrompt,
    isContentItemBlockWidget,
    isContentItemBlogSection,
    isContentItemLeadForm,
} from '../../utils/typeGuards';
import { BlockContent } from '../BlockContent/BlockContent';
import { BlockCta } from '../BlockCta/BlockCta';
import { BlockFaq } from '../BlockFaq/BlockFaq';
import { BlockHeroBanner } from '../BlockHeroBanner/BlockHeroBanner';
import { BlockLegalText } from '../BlockLegalText/BlockLegalText';
import { BlockPromo } from '../BlockPromo/BlockPromo';
import { BlockQuickLink } from '../BlockQuickLink/BlockQuickLink';
import { BlockText } from '../BlockText/BlockText';
import { BlockValuePrompt } from '../BlockValuePrompt/BlockValuePrompt';
import { BlockWidget } from '../BlockWidget/BlockWidget';

const BlockArticles = lazy(() =>
    import('../BlockArticles/BlockArticles').then((mod) => ({ default: mod.BlockArticles })),
);
const BlockProducts = lazy(() =>
    import('../BlockProducts/BlockProducts').then((mod) => ({ default: mod.BlockProducts })),
);
const BlogSection = lazy(() => import('../BlogSection/BlogSection').then((mod) => ({ default: mod.BlogSection })));
const LeadForm = lazy(() => import('../LeadForm/LeadForm').then((mod) => ({ default: mod.LeadForm })));

type Props = {
    contentItem?: AllEntryTypes;
};

const LazyBlock: FC<{ children: ReactNode }> = ({ children }) => <Suspense fallback={null}>{children}</Suspense>;

export const ContentBlock: FC<Props> = ({ contentItem }) => {
    if (isContentItemBanner(contentItem)) {
        return <BlockHeroBanner contentItems={[contentItem]} forcedH2Heading={true} />;
    }
    if (isContentItemBlockContent(contentItem)) {
        return <BlockContent contentItem={contentItem} />;
    }
    if (isContentItemBlockProducts(contentItem)) {
        return (
            <LazyBlock>
                <BlockProducts contentItem={contentItem} />
            </LazyBlock>
        );
    }
    if (isContentItemBlockPromo(contentItem)) {
        return <BlockPromo contentItem={contentItem} />;
    }
    if (isContentItemBlockQuickLink(contentItem)) {
        return <BlockQuickLink contentItem={contentItem} />;
    }
    if (isContentItemBlockText(contentItem)) {
        return <BlockText contentItem={contentItem} />;
    }
    if (isContentItemBlockValuePrompt(contentItem)) {
        return <BlockValuePrompt contentItem={contentItem} />;
    }
    if (isContentItemBlockWidget(contentItem)) {
        return <BlockWidget contentItem={contentItem} />;
    }
    if (isContentItemBlockFaq(contentItem)) {
        return <BlockFaq contentItem={contentItem} />;
    }
    if (isContentItemBlockArticles(contentItem)) {
        return (
            <LazyBlock>
                <BlockArticles contentItem={contentItem} />
            </LazyBlock>
        );
    }
    if (isContentItemBlockCta(contentItem)) {
        return <BlockCta contained={true} contentItem={contentItem} />;
    }
    if (isContentItemBlockLegalText(contentItem)) {
        return <BlockLegalText contentItem={contentItem} />;
    }
    if (isContentItemBlogSection(contentItem)) {
        return (
            <LazyBlock>
                <BlogSection contentItem={contentItem} />
            </LazyBlock>
        );
    }
    if (isContentItemLeadForm(contentItem)) {
        return (
            <LazyBlock>
                <LeadForm contentItem={contentItem} showDefaultOpenModalButton={true} />
            </LazyBlock>
        );
    }

    // unknown entry type
    return null;
};
